import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { GlobalSearch } from './components/GlobalSearch.tsx';
import { ThemeToggle } from './theme/ThemeToggle.tsx';
import { HomePage } from './home/HomePage.tsx';
import { HeaderMore } from './components/HeaderMore.tsx';
import { WelcomeDialog } from './home/WelcomeDialog.tsx';
import { Sidebar } from './nav/Sidebar.tsx';
import { useExitCheckOut } from './attendance/ExitCheckOut.tsx';
import { MobileNav, useNarrow } from './components/MobileNav.tsx';
import { jumpToSection, scrollToAnchor } from './lib/stickySection.ts';
import { get } from './lib/api.ts';
import { SessionProvider, useSession, isAdmin } from './auth/session.tsx';
import { PulseProvider } from './lib/pulse.tsx';
import { LoginPage } from './auth/LoginPage.tsx';
import { CrmPage } from './crm/CrmPage.tsx';
import { useTeamSummary } from './team/useTeamSummary.ts';
import { useChatSummary } from './chat/useChatSummary.ts';
import { useEnrollment } from './attendance/useEnrollment.ts';
import { AttendanceQuickTap } from './attendance/AttendanceQuickTap.tsx';
import { usePendingLeaves } from './admin/attendance/usePendingLeaves.ts';
import { useIdleLogout, IDLE_MINUTES } from './auth/useIdleLogout.ts';
import { FxTicker } from './tools/FxTicker.tsx';
import { ScreenLockGate, ScreenLockSettings } from './account/ScreenLock.tsx';
import { useReportDeviceLocation } from './account/useReportDeviceLocation.ts';
import { AppLock } from './auth/AppLock.tsx';
import { isNativeApp } from './lib/platform.ts';
import { syncDelivered } from './lib/nativePush.ts';
import { NotificationsBell } from './notifications/NotificationsBell.tsx';
import { AnnouncementBanner } from './notifications/AnnouncementBanner.tsx';
import { AnnounceButton } from './notifications/AnnounceButton.tsx';
import { TickerButton } from './notifications/TickerButton.tsx';
import { KpiButton } from './kpi/KpiButton.tsx';
import { Spinner } from './components/ui.tsx';
import { Logo } from './components/Logo.tsx';
import { HeaderIdentity } from './components/HeaderIdentity.tsx';
import { OfflineBanner } from './components/OfflineBanner.tsx';
import { playClickSound } from './notifications/notificationSound.ts';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { TickerBar } from './components/TickerBar.tsx';
import { ChangePasswordPage, ForgotPasswordPage, ResetPasswordPage, tokenFromLocation } from './auth/PasswordPages.tsx';

/**
 * **كل شاشة تُحمَّل حين تُفتح لا كلّها مع أول فتح** (قرار المالك 27/09/2026: «ايه ال يخلى البرنامج سريع دايما
 * مع الشباب»). كان البرنامج ملفًّا واحدًا 1.2 ميجا فيه الخزنة والتحليلات والمرتبات وسجل التدقيق — يُنزّله بياعٌ
 * لن يفتح واحدةً منها. الآن الأساس (الرئيسية، المتابعات، الدخول) في الملف الأوّل، وكلُّ شاشةٍ أخرى ملفٌّ يُطلب عند
 * أوّل فتحٍ لها ويُحفظ. وبعد كل نشرٍ لا يُعاد تنزيل إلا ما تغيّر. وملفٌّ ضاع بعد نشرٍ يعالجه `vite:preloadError`
 * في main.tsx بإعادة تحميلٍ واحدة. ولا شيء يختفي: الشاشات والصلاحيات كما هي.
 */
const AdminCrmPage = lazy(() => import('./crm/AdminCrmPage.tsx').then(m => ({ default: m.AdminCrmPage })));
const BookingsPage = lazy(() => import('./booking/BookingsPage.tsx').then(m => ({ default: m.BookingsPage })));
const DeclarationsPage = lazy(() => import('./booking/DeclarationsPage.tsx').then(m => ({ default: m.DeclarationsPage })));
const UpcomingPage = lazy(() => import('./booking/UpcomingPage.tsx').then(m => ({ default: m.UpcomingPage })));
const AdminPaymentsPage = lazy(() => import('./admin/payments/AdminPaymentsPage.tsx').then(m => ({ default: m.AdminPaymentsPage })));
const AdminSettlementsPage = lazy(() => import('./admin/settlements/AdminSettlementsPage.tsx').then(m => ({ default: m.AdminSettlementsPage })));
const MyCommissionPage = lazy(() => import('./commission/MyCommissionPage.tsx').then(m => ({ default: m.MyCommissionPage })));
const MyPayslipPage = lazy(() => import('./payroll/MyPayslipPage.tsx').then(m => ({ default: m.MyPayslipPage })));
const MyNotesPage = lazy(() => import('./notes/MyNotesPage.tsx').then(m => ({ default: m.MyNotesPage })));
const ChatPage = lazy(() => import('./chat/ChatPage.tsx').then(m => ({ default: m.ChatPage })));
const TeamChatPage = lazy(() => import('./team/TeamChatPage.tsx').then(m => ({ default: m.TeamChatPage })));
const TreasuryPage = lazy(() => import('./treasury/TreasuryPage.tsx').then(m => ({ default: m.TreasuryPage })));
const AttendancePage = lazy(() => import('./attendance/AttendancePage.tsx').then(m => ({ default: m.AttendancePage })));
const AdminAttendancePage = lazy(() => import('./admin/attendance/AdminAttendancePage.tsx').then(m => ({ default: m.AdminAttendancePage })));
const AdminDashboardPage = lazy(() => import('./admin/dashboard/AdminDashboardPage.tsx').then(m => ({ default: m.AdminDashboardPage })));
const MyRankPage = lazy(() => import('./leaderboard/MyRankPage.tsx').then(m => ({ default: m.MyRankPage })));
const EvaluateMePage = lazy(() => import('./evaluation/EvaluateMePage.tsx').then(m => ({ default: m.EvaluateMePage })));
const GoogleRatingPage = lazy(() => import('./rating/GoogleRatingPage.tsx').then(m => ({ default: m.GoogleRatingPage })));
const PricingPage = lazy(() => import('./tools/PricingPage.tsx').then(m => ({ default: m.PricingPage })));
const InstallmentPage = lazy(() => import('./tools/InstallmentPage.tsx').then(m => ({ default: m.InstallmentPage })));
const TafqeetPage = lazy(() => import('./tools/TafqeetPage.tsx').then(m => ({ default: m.TafqeetPage })));
const HijriPage = lazy(() => import('./tools/HijriPage.tsx').then(m => ({ default: m.HijriPage })));
const WorldClockPage = lazy(() => import('./tools/WorldClockPage.tsx').then(m => ({ default: m.WorldClockPage })));
const AirportsPage = lazy(() => import('./tools/AirportsPage.tsx').then(m => ({ default: m.AirportsPage })));
const FxPage = lazy(() => import('./tools/FxPage.tsx').then(m => ({ default: m.FxPage })));
const HotelVoucherPage = lazy(() => import('./tools/HotelVoucherPage.tsx').then(m => ({ default: m.HotelVoucherPage })));
const QuotePage = lazy(() => import('./tools/QuotePage.tsx').then(m => ({ default: m.QuotePage })));
const GuidePage = lazy(() => import('./guide/GuidePage.tsx').then(m => ({ default: m.GuidePage })));
const MyPartnerAccountPage = lazy(() => import('./partner/MyPartnerAccountPage.tsx').then(m => ({ default: m.MyPartnerAccountPage })));
const AuditPage = lazy(() => import('./admin/audit/AuditPage.tsx').then(m => ({ default: m.AuditPage })));
const BackupsPage = lazy(() => import('./admin/backup-centre/BackupsPage.tsx').then(m => ({ default: m.BackupsPage })));
const EmployeesPage = lazy(() => import('./admin/employees/EmployeesPage.tsx').then(m => ({ default: m.EmployeesPage })));
const AnalyticsPage = lazy(() => import('./admin/dashboard/AnalyticsPage.tsx').then(m => ({ default: m.AnalyticsPage })));
const AccountPage = lazy(() => import('./account/AccountPage.tsx').then(m => ({ default: m.AccountPage })));
const AccountPasswordPage = lazy(() => import('./account/AccountPage.tsx').then(m => ({ default: m.AccountPasswordPage })));


type Tab =
  | 'home' | 'crm' | 'bookings' | 'commission' | 'attendance' | 'my-rank' | 'evaluate-me' | 'pricing' | 'clock' | 'fx' | 'hotel-voucher'
  | 'admin-crm' | 'admin-payments' | 'admin-settlements' | 'admin-attendance' | 'admin-dashboard' | 'admin-audit' | 'admin-backups'
  | 'account-password' | 'account-lock' | 'my-partner-account'
  | 'admin-employees' | 'admin-analytics' | 'account' | 'google-rating' | 'installment' | 'guide' | 'upcoming' | 'quote' | 'my-payslip' | 'my-notes' | 'treasury'
  | 'tafqeet' | 'hijri' | 'chat' | 'airports' | 'team-chat' | 'declarations';

/**
 * التبويب المفتوح يعيش في **عنوان الصفحة** (`#tab`) لا في ذاكرة المكوّن وحدها.
 *
 * كان تحديث الصفحة يعيد الموظف إلى أول تبويب مهما كان واقفًا في غيره — وهو ما يحدث كثيرًا:
 * تحديثٌ بعد تسجيل حضور، أو بعد انقطاع شبكة، أو بالخطأ. والعنوان يحفظه بلا تخزين ولا حالة
 * إضافية، ويجعل زرَّي «رجوع» و«تقدّم» في المتصفّح يعملان كما يتوقّعهما المستخدم.
 *
 * والقراءة **محروسة**: ما في العنوان نصٌّ يكتبه أي أحد، فلا يُقبل إلا إن طابق تبويبًا معروفًا.
 */

const TABS: readonly Tab[] = [
  'home', 'crm', 'bookings', 'commission', 'attendance', 'my-rank', 'evaluate-me', 'pricing', 'clock', 'fx', 'hotel-voucher',
  'admin-crm', 'admin-payments', 'admin-settlements', 'admin-attendance', 'admin-dashboard',
  'admin-audit', 'admin-backups', 'admin-employees', 'admin-analytics', 'account',
  'google-rating', 'installment', 'guide', 'upcoming', 'quote', 'my-payslip', 'my-notes', 'treasury',
  'account-password', 'account-lock', 'my-partner-account', 'tafqeet', 'hijri', 'chat', 'airports', 'team-chat', 'declarations',
];

function tabFromHash(): Tab | null {
  try {
    const h = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    return (TABS as readonly string[]).includes(h) ? h as Tab : null;
  } catch { return null; }
}

/** علامةُ «هذه النافذة فُتحت» — في تخزين الجلسة، فتموت بإغلاقها. */
const OPENED_KEY = 'icontravel.opened';

/**
 * **هل هذه فتحةٌ جديدة للبرنامج؟** (قرار المالك 22/09/2026: «أول ما يفتح البرنامج،
 * سواء من الموبايل أو من الـweb، لازم يفتح على الصفحة الرئيسية».)
 *
 * والفتحةُ غيرُ التحديث: من كان يقرأ شاشةً وحدّث الصفحة يريدها كما هي — وهو قرارٌ
 * سابق للمالك نفسه. فالتمييزُ بتخزين الجلسة: يبقى ما دامت النافذة مفتوحة فيُعرف
 * التحديث، ويُمحى بإغلاقها فتُعرف الفتحة الجديدة. وفتحُ التطبيق على الهاتف من جديد
 * جلسةٌ جديدة، فيفتح على الرئيسية كما طُلب.
 *
 * وتُعلَّم عند أول سؤال: فلا تُسأل مرّتين في نفس النافذة.
 */
function isFreshOpen(): boolean {
  try {
    if (window.sessionStorage.getItem(OPENED_KEY) === '1') return false;
    window.sessionStorage.setItem(OPENED_KEY, '1');
    return true;
  } catch { return false; }   // تخزينٌ ممنوع: يبقى ما في العنوان كما كان
}

function Shell() {
  const { me, loading, expired, idle, signOut, signOutIdle, reload } = useSession();
  const [tab, setTab] = useState<Tab>(() => (isFreshOpen() ? 'home' : tabFromHash() ?? 'home'));
  // مدينة الجهاز لـ«أجهزتي» (0193، المالك 06/10/2026) — مرّةً حين يطلبها الخادم، وبإذن صاحب الجهاز
  useReportDeviceLocation(me);

  // كتابة العنوان بلا إضافة خطوة إلى تاريخ التصفّح لكل نقرة… بل بإضافتها عمدًا:
  // «رجوع» يعود إلى التبويب السابق، وهو ما يتوقّعه من اعتاد المتصفّح.
  useEffect(() => {
    try {
      if (tabFromHash() !== tab) window.location.hash = tab;
    } catch { /* بيئة بلا history */ }
  }, [tab]);

  // «رجوع» و«تقدّم» في المتصفّح ينقلان بين التبويبات لا بين صفحات وهمية
  useEffect(() => {
    const onHash = () => { const t = tabFromHash(); if (t) setTab(t); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  // رابط البريد `/reset-password?token=…` — يُفتح بلا جلسة، وهو الطريق الوحيد لتعيين كلمة المرور الأولى
  const [resetToken, setResetToken] = useState<string | null>(() =>
    (typeof window !== 'undefined' && window.location.pathname.replace(/\/+$/, '') === '/reset-password') ? tokenFromLocation() : null);
  const [forgot, setForgot] = useState(false);
  const [changing, setChanging] = useState(false);
  // وحدة الحضور تظهر للمسجَّلين وحدهم. الافتراض مغلق: أثناء التحميل أو عند الفشل لا تظهر،
  // فلا يلمح المالك أو الشريك تبويبًا ليس له. والخادم يرفض بـ403 على كل حال.
  const { enrolled, loading: enrollmentLoading } = useEnrollment(me !== null && !isAdmin(me));
  // طلبات الإجازة المعلّقة — رقم من الخادم، يُقرأ عند فتح الجلسة وعند كل تنقّل وبعد كل قرار
  const { count: pendingLeaves, refresh: refreshPendingLeaves } = usePendingLeaves(me !== null && isAdmin(me), tab);
  // شات الموقع (0117): كم عميلًا ينتظر وكم محادثة عندي — يُسأل كل 10 ثوانٍ ويرنّ حين يظهر منتظرٌ جديد
  const chatBadge = useChatSummary(me !== null && !me.mustChangePassword, tab === 'chat');
  // شات الفريق (0128): رسائل الزملاء غير المقروءة — شارةٌ وصوت خارج شاشته
  const teamBadge = useTeamSummary(me !== null && !me.mustChangePassword, tab === 'team-chat');
  /**
   * موبايل التطبيق: إشعارات الشات تخرج من الشريط والعدد حين **تُقرأ فعلًا** — المحادثة نفسها تزيل وسمها حين تُفتح
   * (TeamChatPage)، وهنا فقط حين ينزل غير المقروء من رقمٍ إلى صفر (قُرئ كله من أي جهاز). لا عند أوّل فتح للتطبيق والعدد
   * لم يُعرف بعد (المالك 03/10/2026: «لازم تتأكد إن الرسالة أو الشخص فتح مصدر الإشعار وشافه»).
   */
  const prevTeamBadge = useRef(0);
  useEffect(() => {
    if (me && prevTeamBadge.current > 0 && teamBadge === 0) void syncDelivered(tag => !tag?.startsWith('team-chat'));
    prevTeamBadge.current = teamBadge;
  }, [me, teamBadge]);
  /**
   * **شاشةٌ هُجرت تُقفل** (قرار المالك 22/09/2026): في مكتبٍ فيه أجهزةٌ مشتركة، من قام ونسي
   * شاشته تركها لمن يمرّ — بحجوزاته وأرقام عملائه ومرتّبه. والمهلة ساعة منذ 01/10/2026 (`IDLE_MINUTES`).
   */
  // الموبايل: صفّ أدوات الكمبيوتر لا يُركَّب أصلًا (02/10/2026)
  const narrowScreen = useNarrow();
  /**
   * **وتطبيق الموبايل لا يُخرج لعدم الاستعمال** (قرار المالك 03/10/2026: «يفتح التطبيق مرة… وبعد كده يفضل شغّال عادي زي
   * الواتساب بحيث يقدر يدخل عليه في أي وقت»): الموبايل شخصيٌّ لا جهاز مكتبٍ مشترك، وحارسه البصمة/الوجه (AppLock) لا الخروج.
   */
  useIdleLogout(me !== null && !isNativeApp(), IDLE_MINUTES, async () => {
    /**
     * **الحاضر لا يُقفل عليه** (قرار المالك 02/10/2026 بعد المراجعة الشاملة: «متخلّيش البرنامج يخرج خالص إلا لو سجّل
     * انصراف — من بداية تسجيله للحضور»): جلسة حضورٍ مفتوحة ⇒ يبقى داخل البرنامج (وأونلاين في دور طلبات الموقع). ومن لا
     * حضور له أو انصرف أو تعذّر السؤال ⇒ القفل بعد ساعة كما كان.
     */
    try { if ((await get<{ openSession?: boolean }>('/attendance/today')).openSession) return false; } catch { /* لا حضور له */ }
    void signOutIdle();
    return true;
  });
  // «خروج» يمرّ على سؤال الانصراف لمن بصم ولم ينصرف (قرار المالك) — ولا يُحبس خروجُ أحد
  const { requestSignOut, exitDialog } = useExitCheckOut(enrolled, signOut);

  if (resetToken !== null) {
    return <ResetPasswordPage token={resetToken} onDone={() => {
      try { window.history.replaceState(null, '', '/'); } catch { /* بيئة بلا history */ }
      setResetToken(null);
    }} />;
  }
  if (loading) return <Spinner label="جارٍ فتح الجلسة…" />;
  if (!me) return forgot ? <ForgotPasswordPage onBack={() => setForgot(false)} /> : <LoginPage onForgot={() => setForgot(true)} expired={expired} idle={idle} />;
  // الخادم يرفض كل مسار بـ403 حتى تُغيَّر كلمة المرور الإلزامية — فلا شاشة قبل هذه
  if (me.mustChangePassword) return <ChangePasswordPage forced onDone={() => void reload()} />;
  if (changing) return <ChangePasswordPage forced={false} onDone={() => setChanging(false)} />;

  /**
   * نقرةٌ مسموعة على أزرار التنقّل (قرار المالك): إحساسٌ بأن الضغطة وصلت قبل أن تُرسم
   * الشاشة الجديدة. وتتبع مفتاح الصوت في الجرس — من كتم كتم الكلّ.
   */
  const pickTab = (id: Tab) => { playClickSound(); setTab(id); };

  // `group`: تبويبات تُطوى في قائمة واحدة بدل أزرار متجاورة (انظر NavMenu)
  const tabs: { id: Tab; label: string; badge?: number; group?: 'tools' | 'settings' }[] = [
    /**
     * **الصفحة الرئيسية أوّلًا** (قرار المالك 22/09/2026): أوّلُ ما يُفتح، وفيها ما يلزم
     * قبل الدخول في شاشة — المستحقّ اليوم، والمتأخّر، والتارجت، وما ينتظر قرارًا.
     * وهي أوّل التبويبات فتكون أوّل وجهةٍ في شريط الموبايل السفلي أيضًا.
     */
    { id: 'home', label: 'الصفحة الرئيسية' },
    { id: 'crm', label: 'المتابعات اليومية' },
    // «شات الموقع» (0117) بعد المتابعات مباشرةً: عميلٌ ينتظر ردًّا الآن — والشارة كم ينتظر + كم عندي
    { id: 'chat' as Tab, label: 'شات الموقع', badge: chatBadge },
    // شات الفريق — جروب الشركة والخاصّ ومين أونلاين (قرار المالك 26/09/2026: «زى جروب الواتس»)
    { id: 'team-chat' as Tab, label: 'شات الفريق', badge: teamBadge },
    { id: 'bookings', label: 'الحجوزات' },
    // إقرار مراجعة بلا حجز (0139، قرار المالك 01/10/2026): «برّا تحت زرّ الحجوزات»
    { id: 'declarations' as Tab, label: 'إقرار مراجعة الطيران' },
    // مستند حجز فندق للتقديم على التأشيرات — ورقةٌ تُطبع ولا حجز يُنشأ (قرار المالك). وفي الأزرار الرئيسية تحت إقرار المراجعة
    // (قرار المالك 03/10/2026: «خلى الحجز المبدئى تحت اقرار مراجعة الطيران ف الزراير الرئيسية») لا في «أدوات ومراجع»
    { id: 'hotel-voucher' as Tab, label: 'حجز فندق مبدئي' },
    // «قادمون» بجانب «الحجوزات» (قرار المالك): هي قراءةُ الحجوزات من زاوية السفر القريب —
    // من نتّصل به اليوم إن تغيّرت الظروف، ومن عليه متبقٍّ قبل أن يسافر.
    { id: 'upcoming' as Tab, label: 'هيسافروا قريب ✈' },
    // «خطة العمولات» للبياع والشريك — الأدمن لا تُعرض له لأنه لا يملك تسويات
    ...(isAdmin(me) ? [] : [{ id: 'commission' as Tab, label: 'خطة العمولات' }]),
    // «تقييم Google Map» بعد «خطة العمولات» مباشرةً (قرار المالك): كلاهما مالُ البياع —
    // نسبتُه وما يُضاف إليها من مكافأة التقييم، فيُقرآن متجاورين.
    ...(isAdmin(me) ? [] : [{ id: 'google-rating' as Tab, label: 'تقييم Google Map' }]),
    // «مرتبي» بجوار خطة العمولات: كلاهما مالُ الموظف — ما يأخذه وما يُخصم منه
    ...(isAdmin(me) ? [] : [{ id: 'my-payslip' as Tab, label: 'مرتبي' }]),
    // «الحضور والانصراف» لموظفي الحضور المسجَّلين وحدهم
    ...(!isAdmin(me) && enrolled ? [{ id: 'attendance' as Tab, label: 'الحضور والانصراف' }] : []),
    // «ترتيبي» للبياع وحده — الشريك خارج التارجت فلا ترتيب له (قرار D-4)، والخادم يرفضه بـ403
    ...(me.role === 'SALES' ? [{ id: 'my-rank' as Tab, label: 'ترتيبي' }] : []),
    // «قيّمني» للبياع وحده (قرار المالك): تقييم متابعاته اليومية على قاعدة ٢٠–٣٠ مكالمة ومسار تارجته
    ...(me.role === 'SALES' ? [{ id: 'evaluate-me' as Tab, label: 'قيّمني' }] : []),
    // تبويبات الأدمن فقط — والخادم يفرض الصلاحية نفسها (403 لغير الأدمن)، فالإخفاء ليس آلية الحماية
    ...(isAdmin(me) ? [
      /**
       * أسماءُ الأربعة كما سمّاها المالك (22/09/2026): «اللوحة والتقارير» صارت
       * «تقارير»، و«تحليل الأداء» صار «KPI · Data Analysis»، و«تقرير التحصيلات» صار
       * «التحصيلات». والاسمُ في الزرّ هو الاسمُ في عنوان الشاشة وفي نتائج البحث —
       * فلا يُسمّى الشيءُ باسمين.
       */
      { id: 'admin-dashboard' as Tab, label: 'تقارير' },
      { id: 'admin-analytics' as Tab, label: 'KPI · Data Analysis' },
      { id: 'admin-crm' as Tab, label: 'نشاط الشركة' },
      { id: 'admin-payments' as Tab, label: 'التحصيلات' },
      { id: 'admin-settlements' as Tab, label: 'الإدارة المالية' },
      // الشارة = طلبات الإجازة المعلّقة التي تنتظر قرار الأدمن، بعددها لا بعلامة ثابتة
      { id: 'admin-attendance' as Tab, label: 'الحضور والإجازات', badge: pendingLeaves },
      { id: 'admin-employees' as Tab, label: 'الموظفين' },
    ] : []),
    /**
     * «دليلك في السياحة» **خارج قائمة الأدوات وللكلّ** (قرار المالك 23/09/2026: «طلّعها برا
     * الـdrop list للكلّ وخلّيها تحت التحصيلات عندي وعند الموظفين تحت هيسافروا قريب»):
     * مرجعٌ يُقرأ كل يوم لا أداةٌ تُفتح عند الحاجة. وموضعُه هنا — بعد كتلة الأدمن — هو ما
     * يجعله في باب الشغل اليومي بعد «التحصيلات» عند الأدمن وبعد «هيسافروا قريب» عند غيره،
     * لأن ترتيب الباب يتبع ترتيب هذه القائمة. ولكلٍّ فيه ملاحظاتُه التي لا يراها غيره.
     */
    { id: 'guide' as Tab, label: 'دليلك في السياحة' },
    // أكواد مطارات العالم (IATA) — قرار المالك 26/09/2026، ثم: «خليها تحت دليلك ف السياحة فى التابات
    // الرئيسية»: مرجعٌ للشغل اليومي بجانب الدليل، لا أداةٌ في القائمة
    { id: 'airports' as Tab, label: 'أكواد المطارات' },
    // «ملاحظاتي» قبل «أدوات هامة» مباشرةً (قرار المالك): آخر ما يخصّ الشخص نفسه
    // قبل أول ما يخصّ الشغل العامّ. ولكل من يدخل البرنامج — والأدمن له دفتره كما لغيره (0086)
    { id: 'my-notes' as Tab, label: 'ملاحظاتي' },
    /**
     * «الخزنة» — **للمالك وحده** (0094): دفتر الكاش الشخصي. والخادم يفرض ذلك
     * (403 لغير المالك)، فالإخفاء هنا راحةٌ للعين لا آليةَ حماية.
     */
    ...(me.isOwner ? [{ id: 'treasury' as Tab, label: '🔒 الخزنة' }] : []),
    /**
     * «حساباتي» — **للشريك وحده** (0100، قرار المالك): أرباحه من المبيعات، وسحوباته،
     * وطلبُ مصروفٍ بإذن المالك. والخادم يقيّد كل قراءةٍ بحسابه هو (403 لغيره).
     */
    // «حساباتي» للشريك الذي أجرُه **عمولة** (طارق ومحمد) لا لمن أجرُه بالوقت (رحمه، 0083) — قرار
    // المالك 23/09/2026: «شيل حساباتي من عند رحمه وخلّيها مرتبي فقط، حساباتي دي لمحمد وطارق فقط»
    ...(me.role === 'PARTNER' && me.payBasis !== 'WORKED_TIME' ? [{ id: 'my-partner-account' as Tab, label: 'حساباتي' }] : []),
    // أدوات عمل للجميع (بياعًا وأدمن) — بعد «الموظفين» بقرار المالك، **مجموعةً في قائمة
    // «أدوات هامة»** لا أزرارًا متجاورة. حسابٌ في المتصفّح وحده: لا مسار خادم ولا صفّ يُكتب
    // «عرض سعر»: ورقة تُسلَّم للعميل قبل الحجز — بنود مفصّلة وصلاحية مكتوبة (قرار المالك)
    { id: 'quote' as Tab, label: 'عرض سعر', group: 'tools' as const },
    { id: 'pricing' as Tab, label: 'حاسبة سعر البيع', group: 'tools' as const },
    // رسوم التقسيط: كم يُسحب من مكنة الـPOS فوق سعر الكاش (قرار المالك)
    { id: 'installment' as Tab, label: 'رسوم التقسيط', group: 'tools' as const },
    // التفقيط والتقويم الهجري (قرار المالك) — أداتان محلّيتان بلا خادم
    { id: 'tafqeet' as Tab, label: 'تفقيط المبالغ', group: 'tools' as const },
    { id: 'hijri' as Tab, label: 'ميلادي ↔ هجري', group: 'tools' as const },
    { id: 'clock' as Tab, label: 'توقيت المدن', group: 'tools' as const },
    { id: 'fx' as Tab, label: 'محوّل العملات', group: 'tools' as const },
    // وشاشتا الأدمن هاتان في قائمة **«إعدادات»** (قرار المالك): تُفتحان عند الحاجة
    // وتُغلقان، فلا تزاحمان تبويبات العمل اليومي.
    // **وإعدادات الحساب كلّها في القائمة الواحدة** (قرار المالك: «خلّي دول في drop list
    // في إعدادات الحساب بتاع الأدمن فقط، مع بيانات الحساب وكلمة مرور الحساب… وضيف عليهم
    // كلمة مرور الخزنة»): خمسة بنود في مكانٍ واحد بدل تبويبٍ وقائمةٍ منفصلين.
    ...(isAdmin(me) ? [
      { id: 'admin-audit' as Tab, label: 'سجل التدقيق', group: 'settings' as const },
      { id: 'admin-backups' as Tab, label: 'النسخ الاحتياطي', group: 'settings' as const },
      { id: 'account' as Tab, label: 'بيانات الحساب', group: 'settings' as const },
      { id: 'account-password' as Tab, label: 'كلمة مرور الحساب', group: 'settings' as const },
      { id: 'account-lock' as Tab, label: 'كلمة مرور الخزنة', group: 'settings' as const },
    ] : [
      // ولغير الأدمن تبويبٌ واحد: ليس عنده سجلّ تدقيق ولا نسخ ولا شاشات مالية تُقفل
      { id: 'account' as Tab, label: 'إعدادات الحساب' },
    ]),
  ];

  /**
   * التبويب المعروض: المطلوب إن كان متاحًا لهذا المستخدم، وإلا فأوّل تبويب عنده.
   *
   * و`tab` يبقى كما هو ولا يُصحَّح: تبويب «الحضور والانصراف» لا يظهر إلا بعد وصول حالة
   * التسجيل من الخادم، فلو صُحِّح فورًا لضاع التبويب المحفوظ في العنوان بعد كل تحديث
   * صفحة. وهكذا يُعرض الأول لحظةً ثم يُفتح المطلوب من تلقائه حين يتاح.
   */
  const active: Tab = tabs.some(t => t.id === tab) ? tab : tabs[0].id;

  return (
    <div className="min-h-screen">
      <OfflineBanner />
      {/* تطبيق الموبايل: قفلٌ بالبصمة/الوجه عند الفتح وبعد الرجوع من الخلفية، وسؤال التفعيل بعد أوّل دخول */}
      {isNativeApp() && <AppLock name={me.fullNameAr} onPasswordLogin={() => void signOut()} />}
      {/* ترحيبٌ عند الدخول، ومعه تذكرةُ البصمة لمن نسيها — مرّةً في اليوم (قرار المالك) */}
      <WelcomeDialog me={me} enrolled={enrollmentLoading ? null : enrolled} />
      {exitDialog}
      {/* الترويسة صفّان **مستقلّان**: الأول للوجو وأدوات الإدارة، والثاني للتنقّل وحده بعرض
          الشاشة كاملًا. الفصل هو ما يجعل التبويبات تتّسع في سطر واحد على سطح المكتب بخطٍّ
          أكبر لا أصغر: أدوات الإدارة لم تعد تقتطع من عرضها. ولا تمرير أفقي ولا أسهم ولا سحب. */}
      {/* الخروج والبصمة يخرجان من اللوح إلى الترويسة (قرار المالك): أوّلُ ما يُعمل وآخرُه
          لا يُبحث عنهما في قائمة. وبقيّة الأدوات تبقى في اللوح. */}
      <MobileNav tabs={tabs} active={active} onPick={id => pickTab(id as Tab)}
        quickTap={!isAdmin(me) && enrolled ? <AttendanceQuickTap /> : undefined}
        search={<GlobalSearch visibleTabs={tabs.map(t => t.id)} canApproveRatings={me.canApproveRatings === true}
          onPick={e => { if (e.page && e.section) jumpToSection(e.page, e.section); pickTab(e.tab as Tab); if (e.anchor) scrollToAnchor(e.anchor); }} />}
        me={me}
        kpi={<KpiButton admin={isAdmin(me)} header />}
        /* الجرس في الترويسة لا في اللوح (06/10/2026) — يُركَّب مع الصفحة فيرنّ ويعدّ والقائمة مقفولة */
        bell={<NotificationsBell compact canDecideLeave={isAdmin(me)} canDecideRating={me.canApproveRatings === true}
          canDecidePartnerExpense={me.isOwner === true} />}
        onSignOut={requestSignOut}>
        <FxTicker />
        {(isAdmin(me) || me.canAnnounce === true) && <AnnounceButton />}
        {isAdmin(me) && <TickerButton />}
        <ThemeToggle />
      </MobileNav>
      {/**
        * **هيكلُ لوحةٍ لا صفحة** (قرار المالك 22/09/2026: «خلّي القوايم رئيسية بدل ما هي
        * أفقية»): عمودُ تنقّلٍ ثابتٌ على اليمين — حيث تبدأ العين العربية — وإلى جانبه
        * ترويسةٌ ومحتوى. وكان التنقّل واحدًا وعشرين زرًّا في سطرين أفقيَّين: صفٌّ بلا
        * بنية، لا يقول أين أنت ولا ما الذي يجاور ما، ويزداد سوءًا مع كل شاشةٍ تُضاف.
        *
        * والهاتف لا يمسّه هذا: شكلُه شريطٌ سفليّ ولوحٌ يُسحب (MobileNav)، وهذا العمود
        * داخل `lg:flex` وحدها.
        */}
      <div className="lg:flex">
      <Sidebar tabs={tabs} active={active} onPick={id => pickTab(id as Tab)} me={me} />
      <div className="flex min-w-0 flex-1 flex-col">
      <header className="z-40 border-b border-slate-200 bg-white/95 backdrop-blur lg:sticky lg:top-0">
        {/* **لا يُركَّب على الموبايل أصلًا** (المراجعة الشاملة 02/10/2026): كان مخفيًّا بالـCSS ومركَّبًا — نسخةٌ ثانية من الجرس
            والبحث وزرّ الحضور وساعة الرأس تعمل في الخلفية: صوتٌ مكرّر، وتذكير حضورٍ يرنّ ولا يظهر، واستطلاعٌ زائد. */}
        {!narrowScreen && (
        // **صفٌّ واحد منخفض** (قرار المالك 04/10/2026: «الاطار ال فوق دا واكل من الشاشه كتير جدا و احنا على وضع 90 عند الناس»):
        // الهوية سطرٌ واحد، والعملات سطرٌ واحد، والشعار صغير، والحشو الرأسي أدنى ما يمكن — فتبقى الشاشة للشغل.
        <div className="hidden w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1 sm:px-6 lg:flex">
          {/* مقاس الشعار: الصندوق يكبر ليكبر **الرسم**. لوحة الملف فيها هامش شفّاف يأكل
              ٢٠٪ من الارتفاع (انظر Logo.tsx)، فالمقروء في الشاشة = ارتفاع الصندوق × ٠٫٧٩٨:
                110px ← رسم ‎88px‏ (سطح المكتب: 1024 فأعلى)
                 88px ← رسم ‎70px‏ (اللوحي: 640–1023)
                 72px ← رسم ‎57px‏ (الهاتف: أقلّ من 640 — عرض الصندوق 90px فلا يكسر 375)
              والصفّ يتمدّد مع الصندوق (`items-center` + `flex-wrap`)، ولا يقتطع من صفّ
              التنقّل شيئًا لأنه صفّ مستقلّ تحته. */}
          {/* الشعار ثم هوية الحساب: الاسم التجاري، ثم اسم النظام، ثم بيانات صاحب الجلسة
              وحده ويومه وساعته. البطاقة تجاور الشعار ولا تكبر على حسابه، و`min-w-0` تسمح
              بانكماش النصّ الطويل (البريد) بدل أن يدفع الصفّ إلى فيض أفقي. */}
          {/**
            * **بطاقة الهوية أصغر لتسع الترويسةُ صفًّا واحدًا على شاشةٍ بلا تصغير**
            * (قرار المالك 22/09/2026: «أنا عاوز الوضع ده والشاشة على 100% عندي» — كان
            * الصفُّ يستقيم بتصغير المتصفّح وحده).
            *
            * وأعرضُ ما في الصفّ بطاقةُ الهوية: اسمٌ تجاريٌّ واسمُ نظامٍ وستّةُ حقول.
            * فتُصغَّر **هنا وحدها** بمحدِّدات الأبناء — والمكوّن نفسه لا يُمسّ، فما
            * يُعرض فيه ولا ترتيبُه يتغيّر، ويبقى كما تختبره header-identity.
            */}
          <span className="flex min-w-0 items-center gap-2">
            {/* الشعار أصغر مما كان (قرار المالك 22/09/2026: «في مساحات فاضية مش مستخدمة
                وواكلة مساحة كبيرة من الشاشة على الفاضي»): صار للبرنامج عمودُ تنقّلٍ يحمل
                اسمه، فلم يعد الشعار وحده حاملَ الهوية في الشاشة. والصفّ كلُّه لا يُرسم
                إلّا فوق 1024px، فمقاساتُ الهاتف فيه كانت أصنافًا ميّتة. */}
            <Logo priority className="h-[2.5rem]" />
            <HeaderIdentity me={me} compact />
          </span>
          {/**
            * **الجرس والإعلان بجانب الاسم لا خلف ⋯** (قرار المالك 22/09/2026: «خلّي دول
            * برّا عندي في الأدمن» — «جنب الاسم كده»).
            *
            * وكانا مطويَّين توفيرًا للعرض، والعرضُ متوفّرٌ هنا: موضعُهما بعد الهوية
            * مباشرةً وقبل «ms-auto» — في الفراغ الذي كان يُهدر وسط الترويسة، فلا
            * يزاحمان أدوات الجلسة على يسارها ولا يدفعان الصفَّ سطرًا.
            *
            * وخارج نطاق تصغير بطاقة الهوية عمدًا: ذلك التصغير لنصِّها هي، وزرّان
            * يُضغطان لا يُصغَّران معه.
            */}
          <span className="flex shrink-0 items-center gap-2">
            <NotificationsBell canDecideLeave={isAdmin(me)} canDecideRating={me.canApproveRatings === true}
              canDecidePartnerExpense={me.isOwner === true} />
            {(isAdmin(me) || me.canAnnounce === true) && <AnnounceButton />}
            {/* «تسجيل الخروج» هنا بجانب الجرس والإعلان لا في آخر الصفّ الثاني (قرار المالك 05/10/2026: «خلى تسجيل الخروج
                جنب اعلان فوق سواء عندى او عند الشباب بدل ماهى مطولة السطر كدا على الفاضى») — وفي الترويسة لا في العمود (22/09/2026) */}
            <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={requestSignOut}>تسجيل الخروج</button>
          </span>
          {/**
            * **أدوات الجلسة تصعد إلى صفّ الهوية ولا تنزل سطرًا** (قرار المالك
            * 22/09/2026: «صغّر الأيقونات والكتابة وخلّيهم فوق ما تنزّلهمش كده — عاوزهم
            * مكان الشخبطة فوق، مش عاوز البار اللي فوق ده ياكل من المساحة كل ده»).
            *
            * وكانت تنزل لأن مجموعها أعرض من المتبقّي بعد الهوية، فيلتفّ الصفّ ويأكل
            * سطرًا كاملًا بينما وسطُ الترويسة فارغ. والمكوّنات نفسها مشتركةٌ مع اللوح
            * في الهاتف فلا تُصغَّر في مصدرها — تُصغَّر **في هذا الموضع وحده**، فتسع
            * الفراغ الذي كان يُهدر.
            */}
          <div className="ms-auto flex flex-wrap items-center gap-2
            [&_.btn-ghost]:min-h-0 [&_.btn-ghost]:px-2.5 [&_.btn-ghost]:py-1 [&_.btn-ghost]:text-xs
            [&_.btn-primary]:min-h-0 [&_.btn-primary]:px-2.5 [&_.btn-primary]:py-1 [&_.btn-primary]:text-xs
            [&_.control]:py-1.5 [&_.control]:text-xs
            [&_[role=search]]:w-44
            [&_.text-sm]:text-xs [&_.text-xs]:text-[0.8125rem]">
            {/* البحث العامّ (قرار المالك): «أدوّر على أي حاجة نسيت مكانها فين — يدخلني عليها على طول» */}
            <GlobalSearch visibleTabs={tabs.map(t => t.id)} canApproveRatings={me.canApproveRatings === true}
              onPick={e => { if (e.page && e.section) jumpToSection(e.page, e.section); pickTab(e.tab as Tab); if (e.anchor) scrollToAnchor(e.anchor); }} />
            {/* حضوري: الحالة والتسجيل في الترويسة — للمسجَّلين في الوحدة وحدهم */}
            {!isAdmin(me) && enrolled && <AttendanceQuickTap />}
            {/**
              * **الظاهرُ ما يُقرأ كل يوم، والباقي خلف زرٍّ واحد** (قرار المالك
              * 22/09/2026: «مش عايز دول جنب بعض كده واخدين مساحة كبيرة»، ثم «خلّي دول
              * مخفيين في تبويب ⋯ نضغط عليه يظهروا»).
              *
              * فيبقى ظاهرًا: سعرُ الصرف — يُقرأ قبل كل تسعيرة — ولوحةُ الأداء للأدمن،
              * وشكلُ الشاشة. ويُطوى: الجرسُ والإعلانُ والشريط المتحرّك.
              *
              * **وطيُّ الجرس لا يُخفي خبرًا**: الجديدُ يقفز بنفسه في تنبيهٍ أسفل
              * الشاشة (NotificationPopup)، وإعلانُ الإدارة يُفتح بنصّه فوق كل صفحة
              * (AnnouncementBanner). فالجرسُ سجلٌّ يُراجَع لا منبّهٌ يُنتظر.
              */}
            <FxTicker compact />
            {isAdmin(me) && <KpiButton admin />}
            <ThemeToggle />
            {/* وبقي خلف ⋯ ما يُفتح حين يُطلب وحده: الشريط المتحرّك */}
            {isAdmin(me) && <HeaderMore><TickerButton /></HeaderMore>}
          </div>
        </div>
        )}
        {/* الشريط المتحرك: إعلان المالك يمشي تحت التنقّل أمام الجميع (قرار المالك) */}
        <TickerBar />
      </header>
      {/* حارس الشاشة: خطأٌ في رسم تبويبٍ يُعرَض رسالةً في مكانه — لا صفحة بيضاء، والترويسة
          والتنقّل يبقيان فيخرج المستخدم منه بنقرة. و`resetKey` يعيد المحاولة عند تبديل التبويب. */}
      <main className="mx-auto w-full max-w-[90rem] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <AnnouncementBanner />
        <ErrorBoundary resetKey={active}><Suspense fallback={<Spinner />}>
        {active === 'home' ? <HomePage me={me} pendingLeaves={pendingLeaves} onGo={id => pickTab(id as Tab)} />
          : active === 'crm' ? <CrmPage />
          : active === 'bookings' ? <BookingsPage />
          : active === 'declarations' ? <DeclarationsPage />
          : active === 'upcoming' ? <UpcomingPage isAdmin={isAdmin(me)} />
          : active === 'commission' ? <MyCommissionPage />
          : active === 'my-notes' ? <MyNotesPage />
          : active === 'chat' ? <ChatPage />
          : active === 'team-chat' ? <TeamChatPage />
          : active === 'my-partner-account' ? <MyPartnerAccountPage />
          : active === 'treasury' ? (
            <ScreenLockGate title="الخزنة"><TreasuryPage /></ScreenLockGate>)
          : active === 'my-payslip' ? <MyPayslipPage />
          : active === 'attendance' ? <AttendancePage />
          : active === 'my-rank' ? <MyRankPage />
          : active === 'evaluate-me' ? <EvaluateMePage />
          : active === 'google-rating' ? <GoogleRatingPage />
          : active === 'quote' ? <QuotePage staffName={me.fullNameAr} />
          : active === 'pricing' ? <PricingPage />
          : active === 'installment' ? <InstallmentPage />
          : active === 'tafqeet' ? <TafqeetPage />
          : active === 'hijri' ? <HijriPage />
          : active === 'clock' ? <WorldClockPage />
          : active === 'airports' ? <AirportsPage />
          : active === 'fx' ? <FxPage />
          : active === 'hotel-voucher' ? <HotelVoucherPage />
          : active === 'guide' ? <GuidePage isAdmin={isAdmin(me)} />
          : active === 'admin-dashboard' ? <ScreenLockGate title="لوحة الأدمن"><AdminDashboardPage /></ScreenLockGate>
          : active === 'admin-analytics' ? <ScreenLockGate title="التحليلات"><AnalyticsPage /></ScreenLockGate>
          : active === 'admin-payments' ? <AdminPaymentsPage />
          : active === 'admin-settlements' ? (
            <ScreenLockGate title="الإدارة المالية">
              <AdminSettlementsPage canApproveRatings={me.canApproveRatings === true} isOwner={me.isOwner === true} />
            </ScreenLockGate>)
          : active === 'account' ? (
            /* الأدمن يغيّرها من بندها في القائمة، والبياع من بطاقةٍ في شاشته — فله تبويبٌ واحد */
            <AccountPage {...(isAdmin(me) ? {} : { onChangePassword: () => setChanging(true) })} />)
          : active === 'account-password' ? <AccountPasswordPage onChangePassword={() => setChanging(true)} />
          : active === 'account-lock' ? <ScreenLockSettings />
          // خلف قفل الشاشة كالخزنة (مراجعة 23/09/2026): النسخة فيها الخزنة كلّها، والسجلّ يقرأ الأصول
          : active === 'admin-audit' ? <ScreenLockGate title="سجل التدقيق"><AuditPage /></ScreenLockGate>
          : active === 'admin-backups' ? <ScreenLockGate title="النسخ الاحتياطي"><BackupsPage /></ScreenLockGate>
          : active === 'admin-employees' ? <EmployeesPage />
          : active === 'admin-attendance' ? <AdminAttendancePage onLeaveDecided={refreshPendingLeaves} />
          : <AdminCrmPage />}
        </Suspense></ErrorBoundary>
      </main>
      </div>
      </div>
      {/* العائم للكمبيوتر وحده — وفي الموبايل في الترويسة (04/10/2026: كان يقع تحت شريط التنقّل) */}
      {!isAdmin(me) && !narrowScreen && <KpiButton floating />}
    </div>
  );
}

export function App() {
  // النبضة (27/09/2026): سؤالٌ واحد كل عشر ثوانٍ لكل العدّادات — بدل خمسة أسئلة منفصلة من كل جهاز
  return <SessionProvider><PulseProvider><Shell /></PulseProvider></SessionProvider>;
}
